/**
 * Polls the server's `/api/version` endpoint to detect when a fresh client
 * bundle has been deployed. Compares the live `webVersion` against the
 * bundled `__APP_VERSION__`; on mismatch (server > client), invokes
 * `onOutdated` so the host can show the existing "Recharger" overlay.
 *
 * Lives outside the existing fetchWrapper / 426 path on purpose:
 *   - 426 only fires when the server BUMPS `MIN_VERSION` and a request
 *     happens to hit a gated route. Players who aren't actively saving
 *     (idle in a menu, mid-fight without auto-save) wouldn't see it.
 *   - This poller fires every 60 s regardless of player activity, and
 *     flips the overlay even when the deploy didn't bump MIN_VERSION
 *     (i.e. soft updates that should still nudge clients to refresh).
 *
 * Idempotent: calling `startVersionPolling` more than once is a no-op
 * after the first call (guards a top-level start handle).
 *
 * Network errors (offline, server hiccup) are swallowed silently so
 * a flaky connection doesn't spam the overlay.
 */
let started = false;

export function startVersionPolling(
  currentVersion: string,
  onOutdated: (liveVersion: string) => void,
  intervalMs = 60_000,
): void {
  if (started) return;
  started = true;

  const tick = async (): Promise<void> => {
    try {
      const res = await fetch('/api/version', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { webVersion?: unknown };
      const live = typeof data.webVersion === 'string' ? data.webVersion : '0.0.0';
      // Strict mismatch — fires for ANY drift, not just live > current.
      // A user-fired soft refresh hits the same path: rebuilt bundle has the
      // new version, server learns it from packages/client/package.json on
      // the next request, future tabs still on the old hash see the prompt.
      if (live !== '0.0.0' && live !== currentVersion) {
        onOutdated(live);
      }
    } catch {
      // Offline / server down — silent, retry on next tick.
    }
  };

  // Fire once after a short delay so the boot scene gets a clean first
  // frame before the network probe goes out, then on the regular cadence.
  setTimeout(() => { void tick(); }, 5_000);
  setInterval(() => { void tick(); }, intervalMs);
}
